import { dayjs } from '@noor/lib';
import {
  requestCategoryName,
  requestEntityId,
  requestEntityName,
  requestParentId,
  requestParentName,
  requestSku,
  type ChangeRequest,
  type ModerationListSearch,
} from '@/entities/moderation';

/**
 * `search`, the date range and the product category narrow the fetched page
 * in memory (the queue endpoint has no text query, date or category params).
 * Rows without a parseable `created_at` stay visible under a date filter —
 * a missing timestamp must not hide a reviewable request. The category
 * matches the row's `parent` (a product's parent is its category).
 */
export function filterRequests(
  requests: ChangeRequest[],
  params: ModerationListSearch,
): ChangeRequest[] {
  const needle = params.search?.trim().toLowerCase();
  const from = parseDayBound(params.dateFrom, 'start');
  const to = parseDayBound(params.dateTo, 'end');
  const categoryId = params.productCategoryId?.trim() ?? '';
  return requests.filter(
    (request) =>
      matchesSearch(request, needle) &&
      matchesDateRange(request, from, to) &&
      (categoryId === '' || requestParentId(request, 'product_category') === categoryId),
  );
}

function matchesSearch(request: ChangeRequest, needle: string | undefined): boolean {
  if (!needle) return true;
  const haystack = [
    requestEntityName(request, ''),
    requestEntityId(request),
    request.id,
    request.merchant_id ?? '',
    request.restaurant_id ?? '',
    requestSku(request) ?? '',
    requestCategoryName(request),
    requestParentName(request, ''),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

function matchesDateRange(
  request: ChangeRequest,
  from: ReturnType<typeof parseDayBound>,
  to: ReturnType<typeof parseDayBound>,
): boolean {
  if (from === undefined && to === undefined) return true;
  const created = dayjs(request.created_at);
  if (!request.created_at || !created.isValid()) return true;
  if (from !== undefined && created.isBefore(from)) return false;
  if (to !== undefined && created.isAfter(to)) return false;
  return true;
}

function parseDayBound(value: string | undefined, edge: 'start' | 'end') {
  if (!value) return undefined;
  const parsed = dayjs(value, 'YYYY-MM-DD', true);
  if (!parsed.isValid()) return undefined;
  return edge === 'start' ? parsed.startOf('day') : parsed.endOf('day');
}
